"use client"
import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { useLazyThirdParty } from "@/hooks/useLazyThirdParty"

interface GHLChatWidgetProps {
  nonce?: string
}

const GHL_HOST_RE = /leadconnectorhq\.com|msgsndr\.com|gohighlevel\.com/i

// Live chat loads on every page. It follows the same posture as the booking widget, GHL forms,
// GA and the Meta Pixel: inject with data-cmp-ab="off" and let the consentmanager CMP govern the
// actual cookies. (The previous UK/EEA gate checked getConsentPrefs()/`bp_consent_required`, but
// the only thing that set that state — the custom CookieConsent banner — is no longer rendered
// anywhere; consentmanager replaced it. So getConsentPrefs() was permanently null and the widget
// never loaded for EEA-flagged visitors, e.g. anyone on a UK/EU VPN.)
//
// The GHL loader.js builds its chat UI as its OWN iframe, which consentmanager's auto-blocker
// rewrites to about:blank (we can't pre-tag an element the third-party loader creates). A
// MutationObserver keeps every GHL element exempt (data-cmp-ab="off") and, if the blocker already
// nulled an injected iframe's src, restores it once from the stashed original. This is a best-effort
// self-heal; the guaranteed fix is whitelisting *.leadconnectorhq.com in the consentmanager account.
export function GHLChatWidget({ nonce = '' }: GHLChatWidgetProps) {
  const loaded = useRef(false)
  // Chat loads on first interaction, not on page load — see useLazyThirdParty.
  const interacted = useLazyThirdParty()
  const pathname = usePathname()
  // The client portal (/track/…) is a client-facing status page — no chat there.
  const onPortal = pathname?.startsWith("/track") ?? false

  // If the widget was already injected on another page, hide it while on the
  // portal (client-side navigation keeps injected DOM alive across routes).
  useEffect(() => {
    document.querySelectorAll<HTMLElement>("[data-chat-widget], chat-widget").forEach(el => {
      el.style.display = onPortal ? "none" : ""
    })
  }, [onPortal])

  useEffect(() => {
    if (!interacted || onPortal) return
    const restored = new WeakSet<Element>()

    function exempt(el: Element) {
      if (!(el instanceof HTMLElement)) return
      const attrsHaveGhl = Array.from(el.attributes).some(a => GHL_HOST_RE.test(a.value))
      const isGhl =
        attrsHaveGhl ||
        el.hasAttribute('data-chat-widget') ||
        el.hasAttribute('data-widget-id') ||
        el.closest('[data-chat-widget]') != null
      if (!isGhl) return
      // Tag BEFORE restoring src so consentmanager skips it on its next observation pass.
      if (el.getAttribute('data-cmp-ab') !== 'off') el.setAttribute('data-cmp-ab', 'off')
      if (
        el instanceof HTMLIFrameElement &&
        !restored.has(el) &&
        /^about:blank$/i.test(el.getAttribute('src') || '')
      ) {
        // consentmanager stashes the real URL on a data-* attribute — restore the first GHL one, once.
        for (const a of Array.from(el.attributes)) {
          if (a.name !== 'src' && GHL_HOST_RE.test(a.value)) {
            restored.add(el)
            el.setAttribute('src', a.value)
            break
          }
        }
      }
    }

    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        if (m.type === 'attributes' && m.target instanceof Element) exempt(m.target)
        m.addedNodes.forEach(n => {
          if (!(n instanceof Element)) return
          exempt(n)
          n.querySelectorAll?.('iframe, script, div').forEach(exempt)
        })
      }
    })
    mo.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    })

    if (!loaded.current) {
      loaded.current = true

      const div = document.createElement("div")
      div.setAttribute("data-chat-widget", "")
      div.setAttribute("data-widget-id", "68c905cf1c15b470ad4f3a1b")
      div.setAttribute("data-location-id", "2YVSGppZ3t1nNSl74HPu")
      div.setAttribute("data-cmp-ab", "off") // exempt from consentmanager auto-blocking (same as GHLBookingWidget)
      document.body.appendChild(div)

      const script = document.createElement("script")
      script.src = "https://widgets.leadconnectorhq.com/loader.js"
      script.dataset.resourcesUrl = "https://widgets.leadconnectorhq.com/chat-widget/loader.js"
      script.dataset.widgetId = "68c905cf1c15b470ad4f3a1b"
      script.setAttribute("data-cmp-ab", "off") // exempt from consentmanager auto-blocking so the loader runs
      script.async = true
      if (nonce) script.nonce = nonce
      document.body.appendChild(script)
    }

    return () => mo.disconnect()
  }, [nonce, interacted, onPortal])

  return null
}
